import React, { useEffect, useState } from "react";
import {
  Description,
  FieldGroup,
  Fieldset,
  Label,
} from "../../../../design/fieldset";
import { Heading, Subheading } from "../../../../design/heading";
import { Switch, SwitchField } from "../../../../design/switch";
import { Input } from "../../../../design/input";
import { Textarea } from "../../../../design/textarea";
import { Select } from "../../../../design/select";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import { Divider } from "../../../../design/divider";
import { useAccount } from "../../../../contexts/AccountContext";
import { AccountService } from "../../../../services/AccountService";
import SettingsHeader from "../SettingsHeader";
import { ReportSettings } from "@visitproof/types";
import {
  DocumentTextIcon,
  ClockIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

// Common US timezones
const TIMEZONE_OPTIONS = [
  { value: "", label: "Use Location Timezone (Default)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { value: "America/Phoenix", label: "Arizona (No DST)" },
  { value: "UTC", label: "UTC" },
];

const DEFAULT_REPORT_SETTINGS: ReportSettings = {
  showCoordinatesInReport: true,
  includeImagesInReport: true,
  reportTimezone: "",
  reportHeaderText: "",
  reportFooterText: "",
};

const ReportsSettings: React.FC = () => {
  const { account } = useAccount();
  const [settings, setSettings] = useState<ReportSettings>(DEFAULT_REPORT_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAI, setIsSavingAI] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const config = (account?.configuration || {}) as Record<string, unknown>;
  const [isAIEnabled, setIsAIEnabled] = useState((config?.isAIEnabled as boolean) ?? false);

  // Initialize settings from account
  useEffect(() => {
    if (account?.reportSettings) {
      setSettings({
        ...DEFAULT_REPORT_SETTINGS,
        ...account.reportSettings,
      });
    }
  }, [account?.reportSettings]);

  useEffect(() => {
    const config = (account?.configuration || {}) as Record<string, unknown>;
    setIsAIEnabled((config?.isAIEnabled as boolean) ?? false);
  }, [account]);

  const handleAIToggle = async (checked: boolean) => {
    if (!account?.id) return;
    setIsAIEnabled(checked);
    setIsSavingAI(true);
    try {
      await AccountService.updateAccount(account.id, {
        configuration: {
          ...(account.configuration || {}),
          isAIEnabled: checked,
        },
      });
    } catch (e) {
      console.error("Error updating AI setting:", e);
      setIsAIEnabled(!checked);
    } finally {
      setIsSavingAI(false);
    }
  };

  const handleToggleChange = async (field: keyof ReportSettings, value: boolean) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    await saveSettings(newSettings);
  };

  const handleTextChange = (field: keyof ReportSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const saveSettings = async (settingsToSave?: ReportSettings) => {
    const dataToSave = settingsToSave || settings;
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await AccountService.updateAccount(account.id, {
        reportSettings: dataToSave,
      });
      setSaveMessage({ type: "success", text: "Settings saved successfully" });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error("Error saving report settings:", error);
      setSaveMessage({ type: "error", text: "Failed to save settings" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTextFields = async () => {
    await saveSettings();
  };

  return (
    <>
      <SettingsHeader />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <Heading>PDF Report Settings</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            Configure how event report PDFs are generated for completed visits.
          </Text>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`p-3 rounded-lg text-sm ${
              saveMessage.type === "success"
                ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        {/* Content Display Options */}
        <Fieldset>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <Subheading>Content Display</Subheading>
          </div>
          <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
            Control what information appears in your event report PDFs.
          </Text>

          <FieldGroup>
            <SwitchField>
              <Label>Show GPS Coordinates</Label>
              <Description>
                Display latitude and longitude coordinates for check-ins and location events.
              </Description>
              <Switch
                name="showCoordinatesInReport"
                checked={settings.showCoordinatesInReport}
                onChange={(checked) => handleToggleChange("showCoordinatesInReport", checked)}
              />
            </SwitchField>

            <SwitchField>
              <Label>Include Photos</Label>
              <Description>
                Include photos and images captured during the visit in the PDF report.
              </Description>
              <Switch
                name="includeImagesInReport"
                checked={settings.includeImagesInReport}
                onChange={(checked) => handleToggleChange("includeImagesInReport", checked)}
              />
            </SwitchField>

            <SwitchField>
              <Label>Show Staff Initials Only</Label>
              <Description>
                Display only staff initials (e.g., "L.S.") instead of full names in reports and visit notes. This can help protect individual staff members from being subpoenaed.
              </Description>
              <Switch
                name="showStaffInitialsOnly"
                checked={settings.showStaffInitialsOnly ?? false}
                onChange={(checked) => handleToggleChange("showStaffInitialsOnly", checked)}
              />
            </SwitchField>
          </FieldGroup>
        </Fieldset>

        <Divider />

        {/* Timezone Settings */}
        <Fieldset>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <ClockIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <Subheading>Timezone</Subheading>
          </div>
          <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
            Set a default timezone for all reports. By default, reports use the timezone of the visit location.
          </Text>

          <FieldGroup>
            <div className="max-w-md">
              <Label>Report Timezone</Label>
              <Text className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">
                Override the location timezone for all PDF reports.
              </Text>
              <Select
                name="reportTimezone"
                value={settings.reportTimezone || ""}
                onChange={(e) => handleTextChange("reportTimezone", e.target.value)}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </Select>
            </div>
          </FieldGroup>
        </Fieldset>

        <Divider />

        {/* Header & Footer */}
        <Fieldset>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <DocumentTextIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <Subheading>Custom Text</Subheading>
          </div>
          <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
            Add custom header and footer text to your PDF reports.
          </Text>

          <FieldGroup>
            <div>
              <Label>Header Text</Label>
              <Text className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">
                Text displayed at the top of each report page (after the title).
              </Text>
              <Input
                name="reportHeaderText"
                placeholder="e.g., Confidential - Do Not Distribute"
                value={settings.reportHeaderText || ""}
                onChange={(e) => handleTextChange("reportHeaderText", e.target.value)}
              />
            </div>

            <div>
              <Label>Footer Text</Label>
              <Text className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">
                Text displayed at the bottom of each report page (before the page number).
              </Text>
              <Textarea
                name="reportFooterText"
                placeholder="e.g., Generated by VisitProof - www.visitproof.com"
                rows={2}
                value={settings.reportFooterText || ""}
                onChange={(e) => handleTextChange("reportFooterText", e.target.value)}
              />
            </div>
          </FieldGroup>
        </Fieldset>

        {/* Save Button for Text Fields */}
        <div className="flex items-center gap-4">
          <Button
            color="blue"
            onClick={handleSaveTextFields}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
          <Text className="text-sm text-zinc-500 dark:text-zinc-400">
            Toggle settings save automatically. Click "Save Changes" to save text field changes.
          </Text>
        </div>

        <Divider />

        {/* AI Report Assistant */}
        <Fieldset>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <SparklesIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <Subheading>AI Report Assistant</Subheading>
          </div>
          <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
            Allow our AI to assist with writing visit reports. The AI can help
            summarize observations, suggest professional language, and ensure
            consistency across your reports.
          </Text>

          <FieldGroup>
            <SwitchField>
              <Label>Enable AI Report Assistant</Label>
              <Description>
                When enabled, monitors can use AI assistance to help draft visit report summaries.
                All AI-generated content is clearly labeled and must be reviewed before submission.
              </Description>
              <Switch
                name="isAIEnabled"
                checked={isAIEnabled}
                onChange={handleAIToggle}
                disabled={isSavingAI}
              />
            </SwitchField>
          </FieldGroup>

          {isAIEnabled && (
            <div className="p-4 mt-4 border rounded-lg bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
              <Text className="text-sm font-medium text-amber-800 dark:text-amber-300">
                By enabling this feature, you confirm that:
              </Text>
              <ul className="mt-2 space-y-1 text-sm list-disc list-inside text-amber-700 dark:text-amber-400">
                <li>
                  Use of AI-assisted report writing is subject to the VisitProof{" "}
                  <a
                    href="https://www.visitproof.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-amber-900 dark:hover:text-amber-200"
                  >
                    Terms of Service
                  </a>.
                </li>
                <li>You have informed your staff that AI assistance is being used for report generation.</li>
                <li>All AI-generated content will be reviewed by qualified staff before submission.</li>
              </ul>
            </div>
          )}
        </Fieldset>

        <Divider />

        {/* Info Section */}
        <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
          <Subheading className="mb-2">About PDF Reports</Subheading>
          <Text className="text-sm text-zinc-600 dark:text-zinc-400">
            Event Report PDFs are automatically generated when a visit is completed.
            These reports include a chronological log of all events recorded during the visit,
            including check-ins, check-outs, notes, and photos.
          </Text>
          <Text className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
            Changes to these settings will apply to new PDF reports generated after saving.
            To update existing reports, you can regenerate the PDF from the visit detail page.
          </Text>
        </div>
      </div>
    </>
  );
};

export default ReportsSettings;
